import {
  createClient,
  ssrExchange,
  fetchExchange,
  mapExchange,
} from '@urql/core';
import { authExchange } from '@urql/exchange-auth';

import { ref } from 'vue';
import { defineNuxtPlugin } from 'nuxt/app';
import { REFRESH_TOKEN_MUTATION } from '@graphql/mutations/auth';
import { useAuthStore } from '@stores/useAuthStore';

const SSRKEY = '__URQL_DATA__';

interface AuthState {
  token: string;
  refreshToken?: string;
}

export default defineNuxtPlugin(nuxtApp => {
  const { vueApp } = nuxtApp;
  const auth = useAuthStore();

  const isServerSide = typeof window === 'undefined';
  const ssrCache = ssrExchange({ isClient: !isServerSide });

  if (isServerSide) {
    nuxtApp.hook('app:rendered', () => {
      nuxtApp.payload[SSRKEY] = ssrCache.extractData();
    });
  }

  const client = createClient({
    url: '/graphql',
    fetchOptions: {
      headers: {
        environment: import.meta.env.VITE_GRAPHQL_ENVIRONMENT,
      },
    },
    exchanges: [
      mapExchange({
        onError(error) {
          if (error.message.toLocaleLowerCase().includes('unauthenticated')) {
            window.location.href = '/login';
          }

          return error;
        },
      }),
      authExchange<AuthState>({
        async getAuth({ authState, mutate }) {
          if (!authState) {
            const token = auth.accessToken;
            const refreshToken = auth.refreshToken;

            return token && refreshToken ? { token, refreshToken } : null;
          }

          if (auth.shouldRefreshToken) {
            const { data } = await mutate(REFRESH_TOKEN_MUTATION, {
              // eslint-disable-next-line camelcase
              input: { refresh_token: auth.refreshToken },
            });

            if (data?.refreshToken) {
              auth.updateTokens(
                data.refreshToken.access_token,
                data.refreshToken.refresh_token,
                Number(data.refreshToken.expires_in),
              );

              if (window) {
                window.location.reload();
              }

              return {
                token: data.refreshToken.access_token,
                refreshToken: data.refreshToken.refresh_token,
              };
            }
          } else if (auth.accessToken) {
            return {
              token: auth.accessToken,
              refreshToken: auth.refreshToken,
            };
          }

          return null;
        },
        addAuthToOperation({ authState, operation }) {
          if (!authState?.token || !auth.accessToken) {
            return operation;
          }

          const fetchOptions = typeof operation.context.fetchOptions === 'function'
            ? operation.context.fetchOptions()
            : operation.context.fetchOptions || {};

          return {
            ...operation,
            context: {
              ...operation.context,
              fetchOptions: () => ({
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: `Bearer ${auth.accessToken || authState?.token}`,
                  role: 'advisor',
                  environment: import.meta.env.VITE_GRAPHQL_ENVIRONMENT,
                },
              }),
            },
          };
        },
        didAuthError({ error }) {
          // If logged in after initialisation of client, this will update authState!
          if (auth.accessToken) {
            return true;
          }

          // check if the error was an auth error (this can be implemented in various ways,
          // e.g. 401 or a special error code)
          return error.graphQLErrors.some(
            gqlError => gqlError.extensions?.code === 'UNAUTHORIZED', // TODO: or forbidden? check code
          );
        },
      }),
      ssrCache,
      fetchExchange,
      mapExchange({
        onError(error) {
          showError(error);
        },
      }),
    ],
  });
  vueApp.provide('$urql', ref(client));
});
